/**
 * Loan Calculator Section
 */
import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';

const loanTypes = [
  { id: 'emergency', label: 'Emergency Loan', max: 2000000, rate: 10, maxMonths: 6 },
  { id: 'development', label: 'Development Loan', max: 30000000, rate: 12, maxMonths: 60 },
  { id: 'education', label: 'Education Loan', max: 5000000, rate: 8, maxMonths: 24 },
  { id: 'chapchap', label: 'Chapchap Loan', max: 1000000, rate: 8, maxMonths: 3 },
];

const LoanCalculator = ({ selectedLoan }) => {
  const [loanType, setLoanType] = useState(selectedLoan?.id || 'emergency');
  const [amount, setAmount] = useState(1500000);
  const [months, setMonths] = useState(6);

  const currentLoan = loanTypes.find((l) => l.id === loanType) || loanTypes[0];

  useEffect(() => {
    if (selectedLoan) {
      setLoanType(selectedLoan.id);
    }
  }, [selectedLoan]);

  // Reducing balance calculation
  const principal = amount;
  const monthlyRate = currentLoan.rate / 100 / 12;
  const totalRepayment =
    monthlyRate === 0
      ? principal
      : (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1) *
      months;
  const totalInterest = totalRepayment - principal;

  const formatCurrency = (value) => {
    return 'Tsh ' + Math.round(value).toLocaleString('en-US');
  };

  const formatCompact = (value) => {
    if (value >= 1000000) {
      return 'Tsh ' + (value / 1000000).toFixed(1) + 'M';
    }
    return 'Tsh ' + Math.round(value).toLocaleString('en-US');
  };

  return (
    <section className="py-8 md:py-8 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951] mb-2">Loan Calculator</h2>
          <p className="text-gray-500 text-sm max-w-xl">
            Estimate your monthly repayments before you apply. All figures use the Reducing Balance method.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-gray-50 rounded-xl p-6 md:p-8">
            {/* Loan Type */}
            <div className="mb-6">
              <label className="block text-gray-700 font-medium text-sm mb-2">Loan Type</label>
              <select
                value={loanType}
                onChange={(e) => {
                  setLoanType(e.target.value);
                  const newLoan = loanTypes.find((l) => l.id === e.target.value);
                  if (newLoan) {
                    setAmount(Math.min(amount, newLoan.max));
                    setMonths(Math.min(months, newLoan.maxMonths));
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-[#2f3951] focus:border-transparent outline-none transition-all"
              >
                {loanTypes.map((lt) => (
                  <option key={lt.id} value={lt.id}>
                    {lt.label} - Max: {formatCompact(lt.max)}
                  </option>
                ))}
              </select>
            </div>

            {/* Loan Amount */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="text-gray-700 font-medium text-sm">Loan Amount</label>
                <span className="text-[#2f3951] font-semibold text-sm">{formatCurrency(amount)}</span>
              </div>
              <input
                type="range"
                min={100000}
                max={currentLoan.max}
                step={50000}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-[#2f3951]"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Tsh 100,000</span>
                <span>{formatCompact(currentLoan.max)}</span>
              </div>
            </div>

            {/* Repayment Period */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="text-gray-700 font-medium text-sm">Repayment Period</label>
                <span className="text-[#2f3951] font-semibold text-sm">{months} Months</span>
              </div>
              <input
                type="range"
                min={1}
                max={currentLoan.maxMonths}
                step={1}
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
                className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-[#2f3951]"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1 Month</span>
                <span>{currentLoan.maxMonths} Months</span>
              </div>
            </div>

            {/* Rate Info */}
            <p className="text-xs text-gray-500">
              <span className="font-medium">Rate:</span> {currentLoan.rate}% reducing balance
            </p>

            {/* Important Disclaimer */}
            <div className="mt-5 border border-amber-300 bg-amber-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-1">
                ⚠ Important Notice
              </p>

              <p className="text-xs md:text-sm text-amber-700 leading-relaxed text-justify">
                Estimates only. Actual loan terms, approval, repayment period, and eligible amount
                are subject to membership standing, credit assessment, and current SACCOS policy.
              </p>
            </div>
          </div>

          {/* Results Panel */}
          <div className="bg-gradient-to-br from-[#2f3951] to-[#3d4a66] rounded-xl p-6 md:p-8 text-white">
            <h3 className="text-lg font-semibold mb-1">{currentLoan.label}</h3>
            <p className="text-2xl md:text-3xl font-bold text-[#ca9a37] mb-6">
              {formatCurrency(totalRepayment)}
            </p>

            <div className="space-y-4">
              {/* Principal */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-300">Principal</span>
                  <span className="font-medium">{formatCurrency(principal)}</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div
                    className="bg-[#ca9a37] h-2 rounded-full transition-all"
                    style={{ width: `${(principal / totalRepayment) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Total Repayment */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-300">Total Repayment</span>
                  <span className="font-medium">{formatCurrency(totalRepayment)}</span>
                </div>
              </div>

              {/* Total Interest */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-300">Total Interest</span>
                  <span className="font-medium">{formatCurrency(totalInterest)}</span>
                </div>
              </div>

              {/* Rate */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-300">Rate</span>
                  <span className="font-medium">{currentLoan.rate}% reducing</span>
                </div>
              </div>

              {/* Repayment Progress */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-300">Repayment</span>
                  <span className="font-medium">{months} months</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div
                    className="bg-[#ca9a37] h-2 rounded-full transition-all"
                    style={{ width: `${(months / currentLoan.maxMonths) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Monthly Estimate */}
              <div className="pt-4 border-t border-white/10">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 text-sm">Monthly Payment (est.)</span>
                  <span className="text-lg font-bold text-[#ca9a37]">
                    {formatCurrency(totalRepayment / months)}
                  </span>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <button className="w-full mt-6 bg-[#ca9a37] hover:bg-[#b88a2f] text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center">
              Apply for this Loan
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoanCalculator;
